import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { TimerProvider } from "./context/TimerContext";
import GlobalRestTimer from "./components/GlobalRestTimer";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import MemberLayout from "./layouts/MemberLayout";
import AdminLayout from "./layouts/AdminLayout";
import HoyPage from "./pages/member/HoyPage";
import SemanaPage from "./pages/member/SemanaPage";
import ProgramaPage from "./pages/member/ProgramaPage";
import HistorialPage from "./pages/member/HistorialPage";
import PlanPage from "./pages/member/PlanPage";
import DietaPage from "./pages/member/DietaPage";
import ProgresoPage from "./pages/member/ProgresoPage";
import CheckinPage from "./pages/member/CheckinPage";
import PerfilPage from "./pages/member/PerfilPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminExercises from "./pages/admin/AdminExercises";
import AdminExerciseLibrary from "./pages/admin/AdminExerciseLibrary";
import AdminPrograms from "./pages/admin/AdminPrograms";
import AdminWorkoutDays from "./pages/admin/AdminWorkoutDays";
import AdminMembers from "./pages/admin/AdminMembers";
import AdminDiets from "./pages/admin/AdminDiets";
import AdminPremiumClients from "./pages/admin/AdminPremiumClients";
import AdminPremiumCRM from "./pages/admin/AdminPremiumCRM";
import AdminRecipes from "./pages/admin/AdminRecipes";
import AdminFoods from "./pages/admin/AdminFoods";
import AdminFlexiblePlans from "./pages/admin/AdminFlexiblePlans";
import AdminCustomPrograms from "./pages/admin/AdminCustomPrograms";
import AdminCustomProgramEdit from "./pages/admin/AdminCustomProgramEdit";
import AdminClientsHub from "./pages/admin/AdminClientsHub";
import AdminAppConfig from "./pages/admin/AdminAppConfig";
import AdminGoatLibrary from "./pages/admin/AdminGoatLibrary";
import PremiumWelcome from "./pages/premium/PremiumWelcome";
import PremiumOnboarding from "./pages/premium/PremiumOnboarding";
import PremiumPending from "./pages/premium/PremiumPending";
import RecetasPage from "./pages/premium/RecetasPage";
import MiNutricionPage from "./pages/premium/MiNutricionPage";
import CheckInFormPage from "./pages/premium/CheckInPage";
import { useEffect, useState } from "react";
import axios from "axios";
import "./App.css";

// App version - CRITICAL: Change this to force cache invalidation
// BUILD: 20260521-1300 - Onboarding fix + GOAT unlock
const APP_BUILD = "v2.4.0-onboarding-fix";

// Log version on load for debugging
console.log(`%c🐐 GOATGYM ${APP_BUILD}`, 'color: #D4AF37; font-weight: bold; font-size: 14px;');

// Clear old caches on app load
const clearOldCaches = async () => {
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    } catch (e) {
      // Cache clear failed - not critical
    }
  }
  
  // Unregister any service workers
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
      }
    } catch (e) {
      // SW unregister failed - not critical
    }
  }
};

const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading, token, isPremium } = useAuth();
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  useEffect(() => {
    // Check if premium user needs onboarding - only once when user loads
    const checkOnboarding = async () => {
      if (user && isPremium && requiredRole === "member" && !onboardingChecked) {
        setCheckingOnboarding(true);
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const response = await axios.get(`${API_URL}/api/premium/onboarding-status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setNeedsOnboarding(response.data.needs_onboarding);
        } catch (error) {
          console.error("Error checking onboarding:", error);
          // On error, don't block - assume onboarding completed
          setNeedsOnboarding(false);
        } finally {
          setCheckingOnboarding(false);
          setOnboardingChecked(true);
        }
      } else if (!isPremium && user) {
        // Non-premium users don't need onboarding check
        setOnboardingChecked(true);
      }
    };
    
    if (user && !loading) {
      checkOnboarding();
    }
  }, [user, isPremium, requiredRole, token, loading, onboardingChecked]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl font-heading">Cargando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/hoy"} replace />;
  }

  // Wait for onboarding check to complete for premium users
  if (isPremium && requiredRole === "member" && !onboardingChecked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl font-heading">Cargando...</div>
      </div>
    );
  }

  // Redirect premium users who need onboarding (except if already on onboarding pages)
  if (needsOnboarding && isPremium && !window.location.pathname.startsWith("/premium/")) {
    return <Navigate to="/premium/welcome" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl font-heading">Cargando...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/hoy"} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public landing page - Main entry point */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/app-entry" element={<LandingPage />} />

      {/* Login page - public but redirects if logged in */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Premium Onboarding Routes */}
      <Route
        path="/premium/welcome"
        element={
          <ProtectedRoute requiredRole="member">
            <PremiumWelcome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/premium/onboarding"
        element={
          <ProtectedRoute requiredRole="member">
            <PremiumOnboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/premium/pending"
        element={
          <ProtectedRoute requiredRole="member">
            <PremiumPending />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recetas"
        element={
          <ProtectedRoute requiredRole="member">
            <RecetasPage />
          </ProtectedRoute>
        }
      />
      
      {/* Mi Nutrición - Premium Flexible Diet */}
      <Route
        path="/mi-nutricion"
        element={
          <ProtectedRoute requiredRole="member">
            <MiNutricionPage />
          </ProtectedRoute>
        }
      />
      
      {/* Premium Check-in Form */}
      <Route
        path="/premium/check-in"
        element={
          <ProtectedRoute requiredRole="member">
            <CheckInFormPage />
          </ProtectedRoute>
        }
      />

      {/* Member routes - protected */}
      <Route
        path="/hoy"
        element={
          <ProtectedRoute requiredRole="member">
            <MemberLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HoyPage />} />
      </Route>
      
      <Route
        path="/semana"
        element={
          <ProtectedRoute requiredRole="member">
            <MemberLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<SemanaPage />} />
      </Route>
      
      <Route
        path="/programa"
        element={
          <ProtectedRoute requiredRole="member">
            <MemberLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ProgramaPage />} />
      </Route>
      
      <Route
        path="/historial"
        element={
          <ProtectedRoute requiredRole="member">
            <MemberLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HistorialPage />} />
      </Route>
      
      {/* Profile route */}
      <Route
        path="/perfil"
        element={
          <ProtectedRoute requiredRole="member">
            <MemberLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PerfilPage />} />
      </Route>
      
      {/* Premium routes */}
      <Route
        path="/plan"
        element={
          <ProtectedRoute requiredRole="member">
            <MemberLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PlanPage />} />
      </Route>
      
      <Route
        path="/plan/dieta"
        element={
          <ProtectedRoute requiredRole="member">
            <MemberLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DietaPage />} />
      </Route>
      
      <Route
        path="/progreso"
        element={
          <ProtectedRoute requiredRole="member">
            <MemberLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ProgresoPage />} />
      </Route>
      
      <Route
        path="/checkin"
        element={
          <ProtectedRoute requiredRole="member">
            <MemberLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<CheckinPage />} />
      </Route>
      
      <Route
        path="/plan/progreso"
        element={
          <ProtectedRoute requiredRole="member">
            <MemberLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ProgresoPage />} />
      </Route>
      
      <Route
        path="/plan/checkin"
        element={
          <ProtectedRoute requiredRole="member">
            <MemberLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<CheckinPage />} />
      </Route>

      {/* Admin routes - protected */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="clients" element={<AdminClientsHub />} />
        <Route path="exercises" element={<AdminExercises />} />
        <Route path="exercise-library" element={<AdminExerciseLibrary />} />
        <Route path="programs" element={<AdminPrograms />} />
        <Route path="workout-days" element={<AdminWorkoutDays />} />
        <Route path="members" element={<AdminMembers />} />
        <Route path="diets" element={<AdminDiets />} />
        <Route path="premium-clients" element={<AdminPremiumClients />} />
        <Route path="premium-crm" element={<AdminPremiumCRM />} />
        <Route path="recipes" element={<AdminRecipes />} />
        <Route path="foods" element={<AdminFoods />} />
        <Route path="flexible-plans" element={<AdminFlexiblePlans />} />
        <Route path="app-config" element={<AdminAppConfig />} />
        <Route path="custom-programs" element={<AdminCustomPrograms />} />
        <Route path="custom-programs/:programId" element={<AdminCustomProgramEdit />} />
        <Route path="goat-library" element={<AdminGoatLibrary />} />
      </Route>

      {/* Fallback - go to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Clear caches on mount
  useEffect(() => {
    clearOldCaches();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <TimerProvider>
          <AppRoutes />
          <GlobalRestTimer />
          <Toaster position="top-center" richColors />
        </TimerProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
